import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { usePendingScope } from "@/lib/pending-edits";
import { userErrorMessage } from "@/lib/user-error";
import { LocalizedError } from "@/lib/localized-error";
import {
  DEFAULT_ACCENT,
  DEFAULT_BG,
  DEFAULT_SURFACE,
  useThemePrefs,
} from "@/stores/theme-prefs";
import { SystemSettingsDocument, UpdateSystemSettingsDocument } from "@/graphql/generated/operations";
import { WelcomeMessageLinkPicker } from "./WelcomeMessageLinkPicker";

// Branding + appearance (genesis systemSettings): app name and the colour
// palette (background, surface, accent), persisted platform-wide in the
// systemSettings JSONB. The palette below applies live via theme-prefs; edits
// surface in the global SaveBar (smycles-toast) and are persisted together on
// Speichern (and hydrated on load via useHydrateThemeFromSystemSettings).
// Logo/background image are deferred until the genesis File resolver is fixed.

function eq(a: string, b: string): boolean {
  return a.toUpperCase() === b.toUpperCase();
}

export function BrandingPanel() {
  const intl = useIntl();
  const { data, loading, refetch } = useQuery(SystemSettingsDocument);
  const [updateSettings] = useMutation(UpdateSystemSettingsDocument);

  const [appName, setAppName] = useState("");
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const welcomeMessageRef = useRef<HTMLTextAreaElement>(null);
  const initialized = useRef(false);
  const settings = data?.systemSettings;
  useEffect(() => {
    if (initialized.current || !settings) return;
    initialized.current = true;
    setAppName(settings.appName ?? "");
    setWelcomeMessage(settings.welcomeMessage ?? "");
  }, [settings]);

  function insertIntoWelcomeMessage(markdown: string) {
    const textarea = welcomeMessageRef.current;
    if (!textarea) {
      setWelcomeMessage((v) => `${v}${markdown}`);
      return;
    }
    const { selectionStart, selectionEnd } = textarea;
    setWelcomeMessage((v) => v.slice(0, selectionStart) + markdown + v.slice(selectionEnd));
    requestAnimationFrame(() => {
      const cursor = selectionStart + markdown.length;
      textarea.focus();
      textarea.setSelectionRange(cursor, cursor);
    });
  }

  // Live palette (theme-prefs); persisted together with the app name.
  const bgColor = useThemePrefs((s) => s.bgColor);
  const surfaceColor = useThemePrefs((s) => s.surfaceColor);
  const accentColor = useThemePrefs((s) => s.accentColor);

  // Persisted baseline (systemSettings); null colours fall back to defaults so
  // a fresh install is not reported as dirty against the local defaults.
  const baseName = settings?.appName ?? "";
  const baseWelcomeMessage = settings?.welcomeMessage ?? "";
  const baseBg = settings?.backgroundColor ?? DEFAULT_BG;
  const baseSurface = settings?.surfaceColor ?? DEFAULT_SURFACE;
  const baseAccent = settings?.accentColor ?? DEFAULT_ACCENT;

  const dirty =
    appName !== baseName ||
    welcomeMessage !== baseWelcomeMessage ||
    !eq(bgColor, baseBg) ||
    !eq(surfaceColor, baseSurface) ||
    !eq(accentColor, baseAccent);

  usePendingScope("branding", {
    dirty,
    save: async () => {
      try {
        await updateSettings({
          variables: {
            input: {
              data: {
                appName: appName.trim(),
                welcomeMessage: welcomeMessage.trim(),
                accentColor,
                backgroundColor: bgColor,
                surfaceColor,
              },
            },
          },
        });
        setAppName((n) => n.trim());
        setWelcomeMessage((m) => m.trim());
        await refetch();
      } catch (e) {
        // Surface a friendly message in the SaveBar (which shows error.message).
        throw new LocalizedError(userErrorMessage(intl, e), { cause: e });
      }
    },
    discard: () => {
      setAppName(baseName);
      setWelcomeMessage(baseWelcomeMessage);
      const store = useThemePrefs.getState();
      store.setBgColor(baseBg);
      store.setSurfaceColor(baseSurface);
      store.setAccentColor(baseAccent);
    },
  });

  return (
    <div className="box bg-surface p-6">
      <div className="text-[10px] tracking-[0.25em] text-ink/55">
        {intl.formatMessage({ id: "adminSettings.branding.title", defaultMessage: "Branding" })}
      </div>
      <div className="mt-1 text-[12px] text-ink/55">
        {intl.formatMessage({
          id: "adminSettings.branding.sub",
          defaultMessage: "App name and colour palette. Applies platform-wide.",
        })}
      </div>

      <div className="mt-5 space-y-4">
        <label className="block">
          <span className="text-[12px] text-ink/55">
            {intl.formatMessage({ id: "adminSettings.branding.appName", defaultMessage: "App name" })}
          </span>
          <input
            value={appName}
            onChange={(e) => setAppName(e.target.value)}
            disabled={loading}
            placeholder={intl.formatMessage({ id: "adminSettings.branding.appNamePlaceholder", defaultMessage: "smycles" })}
            className="bg-surface mt-1 w-full rounded-md px-3 py-2 text-sm ring-1 ring-ink/10 focus:outline-none focus:ring-ink/30 disabled:opacity-60"
          />
          <span className="mt-1 block text-[11px] text-ink/45">
            {intl.formatMessage({
              id: "adminSettings.branding.appNameHint",
              defaultMessage: "Shown top-left. Empty falls back to “smycles”.",
            })}
          </span>
        </label>

        <div>
          <div className="flex items-center justify-between gap-2">
            <label htmlFor="welcomeMessage" className="text-[12px] text-ink/55">
              {intl.formatMessage({
                id: "adminSettings.branding.welcomeMessage",
                defaultMessage: "Welcome message",
              })}
            </label>
            <WelcomeMessageLinkPicker onInsert={insertIntoWelcomeMessage} />
          </div>
          <textarea
            id="welcomeMessage"
            ref={welcomeMessageRef}
            value={welcomeMessage}
            onChange={(e) => setWelcomeMessage(e.target.value)}
            disabled={loading}
            rows={3}
            placeholder={intl.formatMessage({
              id: "adminSettings.branding.welcomeMessagePlaceholder",
              defaultMessage: "Shown as a dismissable hint on the home page.",
            })}
            className="bg-surface mt-1 w-full rounded-md px-3 py-2 text-sm ring-1 ring-ink/10 focus:outline-none focus:ring-ink/30 disabled:opacity-60"
          />
          <span className="mt-1 block text-[11px] text-ink/45">
            {intl.formatMessage({
              id: "adminSettings.branding.welcomeMessageHint",
              defaultMessage: "Supports links: [Text](/path). Everything else is shown as plain text.",
            })}
          </span>
        </div>
      </div>

      <p className="mt-4 text-[11px] text-ink/40">
        {intl.formatMessage({
          id: "adminSettings.branding.paletteNote",
          defaultMessage:
            "The palette below applies live; unsaved changes appear in the save bar and persist platform-wide on save.",
        })}
      </p>
    </div>
  );
}
